import { useCallback, useRef, useState } from "react";

/**
 * Measures an element's content-box width and keeps it up to date via a
 * ResizeObserver. Returns `[ref, width]` where `ref` is a *callback ref* —
 * attach it to the element to measure (`ref={ref}`). Width is `0` until the
 * element mounts and is first measured.
 *
 * A callback ref (rather than `useRef` + `useEffect`) is used so measurement
 * starts the moment the node mounts — even when the node is rendered behind a
 * conditional (e.g. after a loading/error early-return), where a mount-time
 * effect with empty deps would never see it.
 */
export function useElementWidth(): [(node: HTMLDivElement | null) => void, number] {
  const [width, setWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!node) return;

    setWidth(node.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return [ref, width];
}
